import { ObjectPropertyValue } from '../../shared/types'
import { ObjectTypes } from '../api/object'
import { Button } from '../components/button'
import { ObjectPropertyFieldInPlace } from '../components/object-property-field-in-place'
import { getFormatter } from '../objects/formatters'
import { useToggle } from '../use-toggle'
import { ActionModal } from '../widget/action-modal'
import { FormLayout, TableColumn } from './types'

type FormLayoutProps<O extends keyof ObjectTypes> = {
	layout: FormLayout
	data?: ObjectTypes[O]
	onUpdate: (change: Record<string, ObjectPropertyValue>) => void
}

export function FormLayoutInPlace<O extends keyof ObjectTypes>({
	layout,
	data,
	onUpdate,
}: FormLayoutProps<O>) {
	return layout.map((element, index) => {
		switch (element.type) {
			case '__header':
				return (
					<tr key={index} className="text-sm font-bold">
						<th colSpan={2}>{element.text}</th>
					</tr>
				)
			case '__text':
				return (
					<tr key={index} className="text-sm">
						<td colSpan={2}>{element.text}</td>
					</tr>
				)
			case '__button':
				return (
					<tr key={index}>
						<td colSpan={2}>
							<Button variant={element.variant} type="button">
								{element.text}
							</Button>
						</td>
					</tr>
				)
			case 'section':
				return (
					<FormLayoutInPlace
						key={index}
						layout={element.components}
						data={data}
						onUpdate={onUpdate}
					/>
				)
			case 'condition': {
				const matchingCondition = element.conditions.find(
					condition => data?.[condition.when.field] === condition.when.value,
				)

				const layoutToRender = matchingCondition?.then || element.else || []

				return (
					<FormLayoutInPlace
						key={index}
						layout={layoutToRender}
						data={data}
						onUpdate={onUpdate}
					/>
				)
			}
			case 'actionRow':
				return (
					<tr key={index} style={element.style}>
						<th className="whitespace-nowrap">Termín</th>
						<td className="w-full">
							{data && (
								<ActionRow
									action={element.action}
									layout={element.layout}
									parent={data}
									title={element.title}
								/>
							)}
						</td>
					</tr>
				)
			default: {
				return (
					<tr key={index} style={element.style}>
						<th className="whitespace-nowrap">{element.name}</th>
						<td className="w-full">
							<ObjectPropertyFieldInPlace
								key={element.identifier}
								value={data?.[element.identifier]}
								onChange={value => {
									onUpdate({ [element.identifier]: value })
								}}
								data={data}
								{...element}
							/>
						</td>
					</tr>
				)
			}
		}
	})
}

type ActionRowProps = {
	action: string
	layout: {
		field: TableColumn
		name: string
		object: keyof ObjectTypes
	}
	parent: ObjectTypes[keyof ObjectTypes]
	title: string
}

function ActionRow({ action, layout, parent, title }: ActionRowProps) {
	const [isOpen, toggle] = useToggle(false)
	return (
		<div className="flex justify-between items-center">
			<div>
				{getFormatter(layout.field)(parent[layout.field.identifier], layout.field, parent)}
			</div>
			<Button
				variant="TEXT"
				size="SMALL"
				onPress={toggle}
				className="rounded-md hover:bg-blue-700/10 pressed:bg-rose-700/30"
			>
				Upravit
			</Button>
			{isOpen ? (
				<ActionModal
					action={action}
					layout={layout}
					parent={parent}
					title={title}
					onClose={toggle}
				/>
			) : null}
		</div>
	)
}
